import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
    AuthenticationActionService,
    AuthenticationDataService,
    AuthenticationErrorInterface,
    AuthenticationStatusEnum,
    LoginRequestInterface,
    NewModal,
} from 'outshared-lib';
import { Observable, take } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
    selector: 'ins-login-container',
    templateUrl: './login.container.html',
})
export class LoginContainer {
    @Input() public showLoginTitle = false;
    @Output() public readonly afterLogin: EventEmitter<void> = new EventEmitter<void>();

    public loading$: Observable<boolean> = this.authenticationDataService.loginPending$();
    public errors: AuthenticationErrorInterface[] = [];
    public credentials: LoginRequestInterface;
    public showTwoFactor = false;

    constructor(
        private authenticationActionService: AuthenticationActionService,
        private authenticationDataService: AuthenticationDataService,
        private newModal: NewModal
    ) {}

    public onLogin(credentials: LoginRequestInterface): void {
        this.credentials = credentials;
        this.authenticationActionService.login$(credentials).subscribe(
            () => this.afterLogin.emit(),
            (errors: AuthenticationErrorInterface[]) => this.handleErrors(errors)
        );
        return;
    }

    private handleErrors(errors: AuthenticationErrorInterface[]): void {
        const specialStatus: AuthenticationStatusEnum[] = [
            AuthenticationStatusEnum.TemporaryPassword,
            AuthenticationStatusEnum.Blocked,
            AuthenticationStatusEnum.GeneratedAccount,
            AuthenticationStatusEnum.TwoFactor,
        ];

        const statusError = errors.find((error) => specialStatus.includes(error.status));

        if (statusError) {
            this.handleStatus(statusError.status);
        } else {
            this.errors = errors;
        }
        return;
    }

    private async handleStatus(statusCode: AuthenticationStatusEnum): Promise<void> {
        switch (statusCode) {
            case AuthenticationStatusEnum.TemporaryPassword: {
                const { TemporaryPasswordComponent } = await import(
                    '@authentication/feature-account-recovery/temporary-password'
                );
                this.newModal.open(TemporaryPasswordComponent)
                    .afterClosed$()
                    .pipe(take(1), filter((credentials) => Boolean(credentials)))
                    .subscribe((credentials) => this.onLogin(credentials));
                break;
            }
            case AuthenticationStatusEnum.Blocked: {
                const { UnblockAccountComponent } = await import(
                    '@authentication/feature-account-recovery/unblock-account'
                );
                this.newModal.open(UnblockAccountComponent, { data: { credentials: this.credentials } });
                break;
            }
            case AuthenticationStatusEnum.GeneratedAccount: {
                const { ValidateGeneratedAccountComponent } = await import(
                    '@authentication/feature-account-recovery/validate-generated-account'
                );
                this.newModal.open(ValidateGeneratedAccountComponent)
                    .afterClosed$()
                    .pipe(take(1), filter((credentials) => Boolean(credentials)))
                    .subscribe((credentials) => this.onLogin(credentials));
                break;
            }
            case AuthenticationStatusEnum.TwoFactor: {
                this.showTwoFactor = true;
                break;
            }
        }
    }

    onOtpSuccess() {
        this.afterLogin.emit();
    }

    onSessionTimeout() {
        this.showTwoFactor = false;
    }
}
